import React, { useEffect } from 'react';
import { useAuth } from '../context/AuthContext';

/**
 * Lightweight discouragement layer for screenshots of recipes / cost data.
 * Web platform can't *prevent* screenshots, but we can:
 *  - watermark the page with the user's name + email + timestamp + IP-ish hint
 *  - block right-click + image drag/save
 *  - intercept Ctrl/Cmd+P + PrintScreen and show a warning toast
 *
 * Wrap any sensitive page in <ProtectedView>...</ProtectedView>.
 */
export default function ProtectedView({ children, label = 'CONFIDENTIAL · TIRTH INDUSTRIES' }) {
  const { user } = useAuth();
  const stamp = `${label} · ${user?.name || 'unknown'} · ${user?.email || ''} · ${new Date().toLocaleString('en-IN')}`;

  useEffect(() => {
    const noContext = (e) => { e.preventDefault(); return false; };
    const noDrag = (e) => {
      if (e.target?.tagName === 'IMG') { e.preventDefault(); return false; }
      return undefined;
    };
    const onKey = (e) => {
      // Block Ctrl+P / Cmd+P (print) and Ctrl+S / Cmd+S (save) inside protected view
      if ((e.ctrlKey || e.metaKey) && ['p', 's'].includes(e.key.toLowerCase())) {
        e.preventDefault();
        // eslint-disable-next-line no-alert
        alert('Printing / saving this page is disabled. Sensitive data — please contact admin if you need a copy.');
        return false;
      }
      return undefined;
    };
    document.addEventListener('contextmenu', noContext);
    document.addEventListener('dragstart', noDrag);
    document.addEventListener('keydown', onKey);
    document.body.classList.add('no-screenshot');
    return () => {
      document.removeEventListener('contextmenu', noContext);
      document.removeEventListener('dragstart', noDrag);
      document.removeEventListener('keydown', onKey);
      document.body.classList.remove('no-screenshot');
    };
  }, []);

  // Build a tiled watermark via inline SVG-as-CSS background.
  const wmText = encodeURIComponent(stamp);
  const wmUrl = `data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='600' height='240'>
    <text x='50%' y='50%' fill='rgba(245,158,11,0.10)' font-size='13' font-family='monospace'
      transform='rotate(-22 300 120)' text-anchor='middle'>${wmText}</text>
  </svg>`;

  return (
    <div className="relative" data-testid="protected-view">
      {children}
      <div
        aria-hidden="true"
        className="pointer-events-none fixed inset-0 z-[40]"
        style={{
          backgroundImage: `url("${wmUrl}")`,
          backgroundRepeat: 'repeat',
          mixBlendMode: 'lighten',
        }}
      />
    </div>
  );
}
